import Session from "supertokens-auth-react/recipe/session";
import { authService } from "./authService";

/**
 * Unified token helper that abstracts away the difference between
 * logged-in users (SuperTokens) and anonymous users (localStorage).
 */
const ACCESS_TOKEN_MAX_AGE_MS = 10 * 60 * 1000;

function jwtIssuedAtMs(token: string): number | null {
  const parts = token.split(".");
  if (parts.length < 2 || !parts[1]) return null;
  try {
    const normalized = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = normalized.length % 4 === 0 ? "" : "=".repeat(4 - (normalized.length % 4));
    const payload = JSON.parse(atob(normalized + pad)) as { iat?: number };
    return typeof payload.iat === "number" ? payload.iat * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * Reuse the current access token when it is younger than 10 minutes.
 * Refresh only when older (or missing iat). A started upload keeps the token it was given.
 */
export async function getAccessTokenForUpload(): Promise<string | null> {
  const current = await getAccessToken();
  if (!current) return null;

  const issuedAt = jwtIssuedAtMs(current);
  if (issuedAt != null && Date.now() - issuedAt < ACCESS_TOKEN_MAX_AGE_MS) {
    return current;
  }

  try {
    if (await Session.doesSessionExist()) {
      await Session.attemptRefreshingSession();
      return (await Session.getAccessToken()) || current;
    }
  } catch {
    // Keep the current token if refresh is unavailable.
  }

  return current;
}

export async function getAccessToken(): Promise<string | null> {
  // Prefer a real logged-in session when both tokens exist.
  // Anonymous share tokens can remain in localStorage after login/logout,
  // and using them for owner requests can trigger session verification errors.
  const sessionExists = await Session.doesSessionExist();
  if (sessionExists) {
    return (await Session.getAccessToken()) || null;
  }

  const anonymousToken = authService.getAnonymousToken();
  if (anonymousToken) {
    return anonymousToken;
  }

  return null;
}

/**
 * Makes an authenticated request with automatic token handling.
 * Works for both logged-in and anonymous users.
 */
export async function makeAuthenticatedRequest<T>(url: string, options: globalThis.RequestInit = {}): Promise<T> {
  const response = await makeAuthenticatedRequestRaw(url, options);
  return response.json();
}

export async function makeAuthenticatedRequestRaw(
  url: string,
  options: globalThis.RequestInit = {}
): Promise<Response> {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:3000";

  try {
    const accessToken = await getAccessToken();
    if (!accessToken) {
      throw new Error("No active session");
    }

    const headers: Record<string, string> = {
      Authorization: `Bearer ${accessToken}`,
      ...(options.headers as Record<string, string>),
    };

    // Only set Content-Type for JSON requests, not for FormData
    if (options.body && typeof options.body === "string") {
      headers["Content-Type"] = "application/json";
    }

    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers,
    });

    // Check if the server sent an updated JWT token for anonymous users
    const updatedToken = response.headers.get("st-access-token");
    if (updatedToken && authService.isAnonymousUser()) {
      // Update the anonymous token in localStorage
      authService.setAnonymousToken(updatedToken);
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response;
  } catch (error) {
    console.error("Authenticated API request failed:", error);
    throw error;
  }
}

/**
 * Makes an authenticated request to an absolute URL (does not prefix with API_BASE_URL).
 * Useful for downloading media when the backend returns fully-qualified URLs.
 */
export async function fetchAuthenticatedRaw(url: string, options: globalThis.RequestInit = {}): Promise<Response> {
  try {
    const accessToken = await getAccessToken();
    if (!accessToken) {
      throw new Error("No active session");
    }

    const headers: Record<string, string> = {
      Authorization: `Bearer ${accessToken}`,
      ...(options.headers as Record<string, string>),
    };

    // Only set Content-Type for JSON requests, not for FormData
    if (options.body && typeof options.body === "string") {
      headers["Content-Type"] = "application/json";
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Check if the server sent an updated JWT token for anonymous users
    const updatedToken = response.headers.get("st-access-token");
    if (updatedToken && authService.isAnonymousUser()) {
      authService.setAnonymousToken(updatedToken);
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response;
  } catch (error) {
    console.error("Authenticated API request failed:", error);
    throw error;
  }
}

export interface UploadProgressInfo {
  loaded: number;
  total?: number;
}

/**
 * Makes an authenticated multipart/form-data request using XHR to support upload progress.
 * Intended for uploads only (fetch does not provide upload progress events).
 */
export async function makeAuthenticatedUploadRequestWithAbort<T>(
  url: string,
  formData: FormData,
  onProgress?: (info: UploadProgressInfo) => void
): Promise<{ promise: Promise<T>; abort: () => void }> {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:3000";

  const accessToken = await getAccessTokenForUpload();
  if (!accessToken) {
    throw new Error("No active session");
  }

  const xhr = new XMLHttpRequest();

  const promise = new Promise<T>((resolve, reject) => {
    xhr.open("POST", `${API_BASE_URL}${url}`, true);
    xhr.setRequestHeader("Authorization", `Bearer ${accessToken}`);

    xhr.upload.onprogress = (event: ProgressEvent) => {
      if (!onProgress) return;
      const total = (event as ProgressEvent & { total?: number }).total;
      onProgress({
        loaded: event.loaded,
        total: typeof total === "number" && total > 0 ? total : undefined,
      });
    };

    xhr.onload = () => {
      try {
        // Check if the server sent an updated JWT token for anonymous users
        const updatedToken = xhr.getResponseHeader("st-access-token");
        if (updatedToken && authService.isAnonymousUser()) {
          authService.setAnonymousToken(updatedToken);
        }

        const ok = xhr.status >= 200 && xhr.status < 300;
        if (!ok) {
          let message = `HTTP error! status: ${xhr.status}`;
          try {
            const parsed = JSON.parse(xhr.responseText || "{}");
            if (parsed?.message) message = parsed.message;
          } catch {
            // ignore parse errors
          }
          reject(new Error(message));
          return;
        }

        const parsed = JSON.parse(xhr.responseText || "null") as T;
        resolve(parsed);
      } catch (error) {
        reject(error);
      }
    };

    xhr.onerror = () => {
      reject(new Error("Network error while uploading"));
    };

    xhr.onabort = () => {
      reject(new Error("Upload cancelled"));
    };

    xhr.send(formData);
  });

  return {
    promise,
    abort: () => xhr.abort(),
  };
}

export async function makeAuthenticatedUploadRequest<T>(
  url: string,
  formData: FormData,
  onProgress?: (info: UploadProgressInfo) => void
): Promise<T> {
  const { promise } = await makeAuthenticatedUploadRequestWithAbort<T>(url, formData, onProgress);
  return await promise;
}
